import React, { useState, useEffect, useRef } from "react";
import {
  XMarkIcon,
  PhotoIcon,
  CloudArrowUpIcon,
} from "@heroicons/react/24/outline";
import {
  Slider,
  CreateSliderData,
  UpdateSliderData,
  Store,
  OnlineShop,
  apiService,
} from "../../services/api";
import {
  useCreateSlider,
  useUpdateSlider,
  useUploadSliderImage,
} from "../../hooks/useSliders";
import toast from "react-hot-toast";
import StoreSearch from "./StoreSearch";
import OnlineShopSearch from "./OnlineShopSearch";
import CropModal from "./CropModal";

interface SliderFormModalProps {
  slider: Slider | null;
  isOpen: boolean;
  onClose: () => void;
}

const AVAILABLE_COUNTRIES = [
  { code: "DE", name: "Germany" },
  { code: "AT", name: "Austria" },
  { code: "GR", name: "Greece" },
  { code: "IT", name: "Italy" },
];

const AVAILABLE_LANGUAGES = [
  { code: "de", name: "German (Deutsch)" },
  { code: "en", name: "English" },
  { code: "it", name: "Italian (Italiano)" },
  { code: "el", name: "Greek (Ελληνικά)" },
];

const SliderFormModal: React.FC<SliderFormModalProps> = ({
  slider,
  isOpen,
  onClose,
}) => {
  const createSlider = useCreateSlider();
  const updateSlider = useUpdateSlider();
  const uploadImage = useUploadSliderImage();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState({
    imageUrl: "",
    imageUrlCropped: "",
    linkType: "internal" as "internal" | "external" | "online",
    linkValue: "",
    countries: [] as string[],
    language: "de",
    displayOrder: 0,
    isActive: true,
    title: "",
    description: "",
    buttonText: "Learn More",
  });

  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [selectedStore, setSelectedStore] = useState<Store | null>(null);
  const [selectedOnlineShop, setSelectedOnlineShop] =
    useState<OnlineShop | null>(null);
  const [showCropModal, setShowCropModal] = useState(false);
  const [tempImageUrl, setTempImageUrl] = useState<string | null>(null);

  // Helper to get full image URL
  const getImageUrl = (url: string) => {
    if (!url) return "";
    if (url.startsWith("http://") || url.startsWith("https://")) {
      return url;
    }
    const bpeBeUrl =
      process.env.REACT_APP_BPE_BE_URL || "http://localhost:5000";
    return `${bpeBeUrl}${url}`;
  };

  useEffect(() => {
    if (slider) {
      setFormData({
        imageUrl: slider.imageUrl,
        imageUrlCropped: slider.imageUrlCropped || "",
        linkType: slider.linkType,
        linkValue: slider.linkValue,
        countries: slider.countries,
        language: slider.language || "de",
        displayOrder: slider.displayOrder,
        isActive: slider.isActive,
        title: slider.title || "",
        description: slider.description || "",
        buttonText: slider.buttonText || "Learn More",
      });
      // Set preview with full URL
      const imageUrl = slider.imageUrlCropped || slider.imageUrl;
      setPreviewImage(getImageUrl(imageUrl));

      // If internal link, parse and fetch store info
      if (slider.linkType === "internal" && slider.linkValue) {
        // linkValue is just storeId (storeId is unique)
        const storeId = slider.linkValue;
        if (storeId) {
          // Fetch store info to display
          fetchStoreInfo(storeId);
        }
      }
      // If online link, fetch online shop info
      if (slider.linkType === "online" && slider.linkValue) {
        fetchOnlineShopInfo(slider.linkValue);
      }
    } else {
      resetForm();
    }
  }, [slider, isOpen]);

  const fetchStoreInfo = async (storeId: string) => {
    try {
      // Search by store ID to get full store info
      const stores = await apiService.searchStores(storeId);
      const store = stores.find((s) => s.storeId === storeId);
      if (store) {
        setSelectedStore(store);
      }
    } catch (error) {
      console.error("Failed to fetch store info:");
    }
  };

  const fetchOnlineShopInfo = async (storeId: string) => {
    try {
      const shop = await apiService.getOnlineShop(storeId);
      if (shop) {
        setSelectedOnlineShop(shop);
      }
    } catch (error) {
      console.error("Failed to fetch online shop info:");
    }
  };

  const resetForm = () => {
    setFormData({
      imageUrl: "",
      imageUrlCropped: "",
      linkType: "internal",
      linkValue: "",
      countries: [],
      language: "de",
      displayOrder: 0,
      isActive: true,
      title: "",
      description: "",
      buttonText: "Learn More",
    });
    setPreviewImage(null);
    setSelectedStore(null);
    setSelectedOnlineShop(null);
    setShowCropModal(false);
    setTempImageUrl(null);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file
    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image size must be less than 5MB");
      return;
    }

    // Show preview and open crop modal
    const reader = new FileReader();
    reader.onload = (e) => {
      setTempImageUrl(e.target?.result as string);
      setShowCropModal(true);
    };
    reader.readAsDataURL(file);
  };

  const handleCropComplete = async (croppedFile: File) => {
    setShowCropModal(false);

    // Show cropped preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreviewImage(e.target?.result as string);
    };
    reader.readAsDataURL(croppedFile);

    // Auto-upload after cropping
    setUploading(true);
    try {
      const result = await uploadImage.mutateAsync({
        file: croppedFile,
        language: formData.language || "de",
        cropWidth: 1600,
        cropHeight: 900,
      });

      setFormData((prev) => ({
        ...prev,
        imageUrl: result.imageUrl,
        imageUrlCropped: result.imageUrlCropped || "",
      }));

      toast.success("Image uploaded successfully");
    } catch (error) {
      console.error("Upload error:");
      setPreviewImage(null);
    } finally {
      setUploading(false);
    }
  };

  const handleCropCancel = () => {
    setShowCropModal(false);
    setTempImageUrl(null);
  };

  const handleCountryToggle = (countryCode: string) => {
    setFormData((prev) => ({
      ...prev,
      countries: prev.countries.includes(countryCode)
        ? prev.countries.filter((c) => c !== countryCode)
        : [...prev.countries, countryCode],
    }));
  };

  const handleStoreSelect = (store: Store) => {
    setSelectedStore(store);
    // Format: storeId only (storeId is unique)
    const linkValue = store.storeId;
    setFormData((prev) => ({ ...prev, linkValue }));
  };

  const handleOnlineShopSelect = (shop: OnlineShop) => {
    setSelectedOnlineShop(shop);
    // Store only storeId (online shop data can change, so we fetch fresh on mobile)
    setFormData((prev) => ({ ...prev, linkValue: shop.storeId }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!formData.imageUrl) {
      toast.error("Please upload an image");
      return;
    }

    if (formData.linkType === "internal" && !selectedStore) {
      toast.error("Please select a store");
      return;
    }

    if (formData.linkType === "online" && !selectedOnlineShop) {
      toast.error("Please select an online shop");
      return;
    }

    if (!formData.linkValue) {
      toast.error("Please enter a link value");
      return;
    }

    if (formData.countries.length === 0) {
      toast.error("Please select at least one country");
      return;
    }

    try {
      if (slider) {
        // Update
        await updateSlider.mutateAsync({
          id: slider._id,
          data: formData as UpdateSliderData,
        });
      } else {
        // Create
        await createSlider.mutateAsync(formData as CreateSliderData);
      }
      onClose();
    } catch (error) {
      console.error("Submit error:");
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Backdrop */}
        <div
          className="fixed inset-0 transition-opacity bg-gray-500 dark:bg-gray-900 bg-opacity-75 dark:bg-opacity-75"
          onClick={onClose}
        />

        {/* Modal */}
        <div className="inline-block w-full max-w-4xl px-4 pt-5 pb-4 overflow-hidden text-left align-bottom transition-all transform bg-white dark:bg-gray-800 rounded-lg shadow-xl sm:my-8 sm:align-middle sm:p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
              {slider ? "Edit Slide" : "Create New Slide"}
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left Column - Image Upload */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Slide Image *
                  </label>
                  <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-4">
                    {previewImage && formData.imageUrl ? (
                      <div className="relative">
                        <img
                          src={
                            uploading
                              ? previewImage
                              : getImageUrl(
                                  formData.imageUrlCropped || formData.imageUrl,
                                )
                          }
                          alt="Preview"
                          className="w-full h-48 object-cover rounded"
                        />
                        {uploading && (
                          <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 rounded">
                            <div className="text-center">
                              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mb-2 mx-auto" />
                              <p className="text-sm text-white">Uploading...</p>
                            </div>
                          </div>
                        )}
                        {!uploading && (
                          <button
                            type="button"
                            onClick={() => fileInputRef.current?.click()}
                            className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 opacity-0 hover:opacity-100 transition-opacity rounded"
                          >
                            <CloudArrowUpIcon className="h-12 w-12 text-white" />
                          </button>
                        )}
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="w-full h-48 flex flex-col items-center justify-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                        disabled={uploading}
                      >
                        <PhotoIcon className="h-12 w-12 mb-3" />
                        <p className="text-sm">Click to upload image</p>
                        <p className="text-xs mt-1">
                          Recommended: 1600x900px (16:9)
                        </p>
                        <p className="text-xs text-gray-400">Max 5MB</p>
                      </button>
                    )}
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                    Content language for title, description, and button text
                  </p>
                </div>

                {/* Language */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Language *
                  </label>
                  <select
                    value={formData.language}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        language: e.target.value,
                      }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white"
                  >
                    {AVAILABLE_LANGUAGES.map((lang) => (
                      <option key={lang.code} value={lang.code}>
                        {lang.name}
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Content language for title, description, and button text
                  </p>
                </div>

                {/* Countries */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Countries * ({formData.countries.length} selected)
                  </label>
                  <div className="border border-gray-300 dark:border-gray-600 rounded-lg p-4 max-h-60 overflow-y-auto">
                    <div className="space-y-2">
                      {AVAILABLE_COUNTRIES.map((country) => (
                        <label
                          key={country.code}
                          className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 p-2 rounded"
                        >
                          <input
                            type="checkbox"
                            checked={formData.countries.includes(country.code)}
                            onChange={() => handleCountryToggle(country.code)}
                            className="rounded text-primary-600 focus:ring-primary-500"
                          />
                          <span className="text-sm text-gray-700 dark:text-gray-300">
                            {country.name} ({country.code})
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Max 7 active sliders per country
                  </p>
                </div>
              </div>

              {/* Right Column - Form Fields */}
              <div className="space-y-4">
                {/* Link Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Link Type *
                  </label>
                  <div className="flex space-x-4">
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="radio"
                        name="linkType"
                        value="internal"
                        checked={formData.linkType === "internal"}
                        onChange={(e) => {
                          setFormData((prev) => ({
                            ...prev,
                            linkType: "internal",
                            linkValue: "",
                          }));
                          setSelectedStore(null);
                        }}
                        className="text-primary-600 focus:ring-primary-500"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">
                        Internal (Store Link)
                      </span>
                    </label>
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="radio"
                        name="linkType"
                        value="external"
                        checked={formData.linkType === "external"}
                        onChange={(e) => {
                          setFormData((prev) => ({
                            ...prev,
                            linkType: "external",
                            linkValue: "",
                          }));
                          setSelectedStore(null);
                        }}
                        className="text-primary-600 focus:ring-primary-500"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">
                        External (URL)
                      </span>
                    </label>
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="radio"
                        name="linkType"
                        value="online"
                        checked={formData.linkType === "online"}
                        onChange={(e) => {
                          setFormData((prev) => ({
                            ...prev,
                            linkType: "online",
                            linkValue: "",
                          }));
                          setSelectedStore(null);
                          setSelectedOnlineShop(null);
                        }}
                        className="text-primary-600 focus:ring-primary-500"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">
                        Online Shop
                      </span>
                    </label>
                  </div>
                </div>

                {/* Link Value - Store Search for Internal, Online Shop Search for Online, Text Input for External */}
                {formData.linkType === "internal" && (
                  <div>
                    <StoreSearch
                      onSelect={handleStoreSelect}
                      selectedStore={selectedStore}
                    />
                    {selectedStore && (
                      <div className="mt-2 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                        <p className="text-xs font-medium text-blue-900 dark:text-blue-300 mb-1">
                          Link Information
                        </p>
                        <div className="space-y-1">
                          <p className="text-xs text-blue-800 dark:text-blue-400">
                            <span className="font-medium">Store ID:</span>{" "}
                            {selectedStore.storeId}
                          </p>
                          <p className="text-xs text-blue-800 dark:text-blue-400">
                            <span className="font-medium">Dealer ID:</span>{" "}
                            {selectedStore.dealerId}
                          </p>
                          <p className="text-xs text-gray-600 dark:text-gray-400 mt-2 pt-2 border-t border-blue-200 dark:border-blue-700">
                            <span className="font-medium">Link Value:</span>{" "}
                            {formData.linkValue}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {formData.linkType === "online" && (
                  <div>
                    <OnlineShopSearch
                      onSelect={handleOnlineShopSelect}
                      selectedShop={selectedOnlineShop}
                    />
                    {selectedOnlineShop && (
                      <div className="mt-2 p-3 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg">
                        <p className="text-xs font-medium text-purple-900 dark:text-purple-300 mb-1">
                          Online Shop Information
                        </p>
                        <div className="space-y-1">
                          <p className="text-xs text-purple-800 dark:text-purple-400">
                            <span className="font-medium">Store ID:</span>{" "}
                            {selectedOnlineShop.storeId}
                          </p>
                          {selectedOnlineShop.cashbackRate && (
                            <p className="text-xs text-purple-800 dark:text-purple-400">
                              <span className="font-medium">Cashback:</span>{" "}
                              {selectedOnlineShop.cashbackRate}%
                            </p>
                          )}
                          <p className="text-xs text-gray-600 dark:text-gray-400 mt-2 pt-2 border-t border-purple-200 dark:border-purple-700">
                            <span className="font-medium">Link Value:</span>{" "}
                            {formData.linkValue}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {formData.linkType === "external" && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      External URL *
                    </label>
                    <input
                      type="text"
                      value={formData.linkValue}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          linkValue: e.target.value,
                        }))
                      }
                      placeholder="https://example.com"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white"
                    />
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Enter full URL including https://
                    </p>
                  </div>
                )}

                {/* Button Text */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Button Text *
                  </label>
                  <input
                    type="text"
                    value={formData.buttonText}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        buttonText: e.target.value,
                      }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white"
                  />
                </div>

                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Title (Optional)
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        title: e.target.value,
                      }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white"
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Description (Optional)
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        description: e.target.value,
                      }))
                    }
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white"
                  />
                </div>

                {/* Display Order */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Display Order
                  </label>
                  <input
                    type="number"
                    value={formData.displayOrder}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        displayOrder: parseInt(e.target.value) || 0,
                      }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white"
                  />
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Lower numbers appear first
                  </p>
                </div>

                {/* Active Status */}
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="isActive"
                    checked={formData.isActive}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        isActive: e.target.checked,
                      }))
                    }
                    className="rounded text-primary-600 focus:ring-primary-500"
                  />
                  <label
                    htmlFor="isActive"
                    className="ml-2 text-sm text-gray-700 dark:text-gray-300"
                  >
                    Active (visible to customers)
                  </label>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200 dark:border-gray-700">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={
                  uploading || createSlider.isPending || updateSlider.isPending
                }
                className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {uploading
                  ? "Uploading..."
                  : createSlider.isPending || updateSlider.isPending
                    ? "Saving..."
                    : slider
                      ? "Update Slide"
                      : "Create Slide"}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Crop Modal */}
      {tempImageUrl && (
        <CropModal
          isOpen={showCropModal}
          imageUrl={tempImageUrl}
          onClose={handleCropCancel}
          onCropComplete={handleCropComplete}
        />
      )}
    </div>
  );
};

export default SliderFormModal;
